import type { TravelContinent } from "./worldCountries";

export const GAMIFICATION_POINTS = {
  visitedCountry: 10,
  quizAnswer: 1,
  travelFeedback: 5,
  completedTrip: 40,
  publicReview: 1,
  helpfulVote: 2,
  notHelpfulVote: -1
} as const;

export type GamificationLevelId = "starter" | "explorer" | "expert" | "globetrotter";
export type GamificationBadgeId = "explorer" | "topContributor" | "asiaExpert" | "quizMaster" | "reviewer" | "veteran";

export interface GamificationBreakdown {
  visitedCountries: number;
  quizAnswers: number;
  travelFeedbacks: number;
  completedTrips: number;
  publicReviews: number;
  helpfulVotes: number;
  notHelpfulVotes: number;
  asiaCountries: number;
}

export interface GamificationBadgeProgress {
  id: GamificationBadgeId;
  value: number;
  target: number;
  earned: boolean;
}

export function gamificationLevel(score: number): GamificationLevelId {
  if (score >= 500) return "globetrotter";
  if (score >= 250) return "expert";
  if (score >= 100) return "explorer";
  return "starter";
}

export function gamificationBadges(breakdown: GamificationBreakdown): GamificationBadgeProgress[] {
  return [
    {
      id: "explorer",
      value: breakdown.visitedCountries,
      target: 10,
      earned: breakdown.visitedCountries >= 10
    },
    {
      id: "topContributor",
      value: breakdown.helpfulVotes,
      target: 10,
      earned: breakdown.helpfulVotes >= 10
    },
    {
      id: "asiaExpert",
      value: breakdown.asiaCountries,
      target: 10,
      earned: breakdown.asiaCountries >= 10
    },
    {
      id: "quizMaster",
      value: breakdown.quizAnswers,
      target: 16,
      earned: breakdown.quizAnswers >= 16
    },
    {
      id: "reviewer",
      value: breakdown.publicReviews,
      target: 10,
      earned: breakdown.publicReviews >= 10
    },
    {
      id: "veteran",
      value: breakdown.completedTrips,
      target: 10,
      earned: breakdown.completedTrips >= 10
    }
  ];
}

export function gamificationBaseScore(breakdown: GamificationBreakdown): number {
  return (
    breakdown.visitedCountries * GAMIFICATION_POINTS.visitedCountry +
    breakdown.quizAnswers * GAMIFICATION_POINTS.quizAnswer +
    breakdown.travelFeedbacks * GAMIFICATION_POINTS.travelFeedback +
    breakdown.completedTrips * GAMIFICATION_POINTS.completedTrip +
    breakdown.publicReviews * GAMIFICATION_POINTS.publicReview
  );
}

export function gamificationVotePoints(breakdown: GamificationBreakdown): number {
  return (
    breakdown.helpfulVotes * GAMIFICATION_POINTS.helpfulVote +
    breakdown.notHelpfulVotes * GAMIFICATION_POINTS.notHelpfulVote
  );
}

export function gamificationScore(breakdown: GamificationBreakdown): { baseScore: number; votePoints: number; score: number } {
  const baseScore = gamificationBaseScore(breakdown);
  const votePoints = gamificationVotePoints(breakdown);
  return { baseScore, votePoints, score: Math.max(0, baseScore + votePoints) };
}

export function emptyGamificationBreakdown(): GamificationBreakdown {
  return {
    visitedCountries: 0,
    quizAnswers: 0,
    travelFeedbacks: 0,
    completedTrips: 0,
    publicReviews: 0,
    helpfulVotes: 0,
    notHelpfulVotes: 0,
    asiaCountries: 0
  };
}

export function isAsiaContinent(continent: TravelContinent): boolean {
  return continent === "asia";
}
